import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Factory, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    api.get("/demo-accounts").then((r) => setDemoAccounts(r.data.accounts || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`مرحباً ${user.name}`);
      nav("/app");
    } catch (e) {
      setErr(formatApiError(e));
    }
    setLoading(false);
  };

  const applyDemo = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left panel - form */}
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8" data-testid="login-brand-link">
            <div className="w-9 h-9 rounded-md bg-brand-navy flex items-center justify-center">
              <Factory className="w-5 h-5 text-brand-amber" />
            </div>
            <div>
              <div className="font-bold text-brand-navy">MERQAV</div>
              <div className="text-[11px] text-slate-500">MERQAV</div>
            </div>
          </Link>

          <h1 className="text-3xl font-bold text-brand-navy mb-2">تسجيل الدخول</h1>
          <p className="text-slate-600 mb-8">أدخل بياناتك للوصول إلى منصة MERQAV</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">كلمة المرور</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5 h-11" />
            </div>
            {err && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3" data-testid="login-error">{err}</div>}
            <Button type="submit" disabled={loading} className="btn-cta w-full h-11" data-testid="login-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "دخول"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            ليس لديك حساب؟{" "}
            <Link to="/register" className="text-brand-amber font-semibold hover:underline" data-testid="goto-register-link">
              إنشاء حساب جديد
            </Link>
          </div>

          {/* Demo accounts */}
          <div className="mt-8" data-testid="demo-accounts-section">
            <div className="text-xs font-bold tracking-widest text-slate-500 mb-3">حسابات تجريبية</div>
            <div className="grid gap-2">
              {demoAccounts.map((acc) => (
                <button
                  key={acc.email}
                  onClick={() => applyDemo(acc)}
                  className="text-start bg-white border border-slate-200 hover:border-brand-amber rounded-md p-3 text-sm transition-colors flex items-center justify-between"
                  data-testid={`demo-account-${acc.role}-${acc.email}`}
                >
                  <div>
                    <div className="font-semibold text-brand-navy">{acc.label_ar}</div>
                    <div className="text-xs text-slate-500">{acc.email}</div>
                  </div>
                  <ArrowLeft className="w-4 h-4 text-brand-amber rtl-flip" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right - imagery */}
      <div className="hero-blueprint hidden lg:flex flex-col justify-between p-12 text-white order-1 lg:order-2">
        <Link to="/" className="text-sm text-slate-300 hover:text-white">← العودة للرئيسية</Link>
        <div>
          <h2 className="text-4xl font-bold mb-4">نربط الصناعة... ونبني الثقة.</h2>
          <div className="text-sm text-slate-400 mb-3">Connecting Industry. Building Trust.</div>
          <p className="text-slate-300 leading-relaxed text-lg">
            منصة MERQAV تُدير كامل عملية تصنيع القطع الصناعية بسرية تامة وجودة عالية،
            من الفحص الفني حتى التسليم النهائي.
          </p>
        </div>
        <div className="text-sm text-slate-400">© MERQAV — MERQAV</div>
      </div>
    </div>
  );
}
